import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthPanel from '../components/auth/AuthPanel.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useApp } from '../contexts/AppContext.jsx';

function useNextPath() {
  const { search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    const next = params.get('next');
    if (!next) return '/dashboard/jobs';
    // Only allow same-origin paths to avoid open redirect.
    if (!next.startsWith('/')) return '/dashboard/jobs';
    return next;
  }, [search]);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const nextPath = useNextPath();
  const { user, loading: authLoading } = useAuth();
  const { interviewee, loading: appLoading } = useApp();

  useEffect(() => {
    if (authLoading || appLoading) return;
    if (!user) return;
    if (interviewee?.career_onboarding_step === 'complete') {
      navigate(nextPath, { replace: true });
    } else {
      navigate('/onboarding', { replace: true });
    }
  }, [authLoading, appLoading, user, interviewee, navigate, nextPath]);

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,#CCFBF1_0,#F8FAFC_38%,#F1F5F9_100%)] px-4 py-10 text-slate-700 sm:px-6">
      <div className="mx-auto flex w-full max-w-md flex-col">
        <div className="mb-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700">Pareto Career Agency</p>
          <h1 className="mt-3 font-display text-[36px] leading-[1.05] text-obsidian-400 sm:text-[44px]">
            Sign in to Pareto
          </h1>
          <p className="mt-3 text-base leading-7 text-slate-600">
            Get paid for research interviews — and let AI find you a job while you wait.
          </p>
        </div>

        <section className="rounded-[16px] border border-white/70 bg-white/95 p-5 shadow-2xl shadow-slate-900/10 backdrop-blur sm:p-7">
          <AuthPanel mode="signin" nextPath={nextPath} />
        </section>

        <p className="mt-6 text-center text-xs text-slate-500">
          By continuing you agree to the Pareto{' '}
          <a
            href="/terms"
            className="font-semibold text-teal-700 underline-offset-2 hover:underline"
          >
            Terms
          </a>{' '}
          and acknowledge the{' '}
          <a
            href="/privacy"
            className="font-semibold text-teal-700 underline-offset-2 hover:underline"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </main>
  );
}
